import * as L from "leaflet";

export interface PMDrawInstance {
  _layer?: L.Layer;
  _markers?: L.Marker[];
  _vertexMarkers?: L.Marker[];
  _tempMarkers?: L.Marker[];
  markers?: L.Marker[];
  [key: string]: any;
}

/**
 * Safely removes the last vertex from a PM drawing instance
 * Handles both coordinate and visual marker synchronization
 */
export const removeLastVertex = (
  map: L.Map,
  drawInstance: PMDrawInstance,
  shapeName: string
): boolean => {
  const layer = drawInstance._layer;
  if (!layer) return false;

  const latlngs = (layer as any).getLatLngs();
  if (!latlngs || latlngs.length === 0) return false;

  // Update layer coordinates
  latlngs.pop();
  (layer as any).setLatLngs(latlngs);

  // Try multiple marker array patterns for compatibility
  const markerArrayNames = ["_markers", "_vertexMarkers", "_tempMarkers", "markers"];
  let markerRemoved = false;

  for (const arrayName of markerArrayNames) {
    if (drawInstance[arrayName] && drawInstance[arrayName].length > 0) {
      const lastMarker = drawInstance[arrayName].pop();
      if (lastMarker) {
        map.removeLayer(lastMarker);
        markerRemoved = true;
        break;
      }
    }
  }

  // Force redraw if marker sync failed
  if (!markerRemoved) {
    console.warn(`PM marker sync failed for ${shapeName}, forcing redraw`);
    return false;
  }

  // Update visual display
  (layer as any).redraw?.();

  // Try multiple PM internal update methods
  const updateMethods = ["_createVertexGuard", "_updateGuides", "_updateMarkers", "updatePolygon"];
  for (const method of updateMethods) {
    if (typeof drawInstance[method] === "function") {
      try {
        drawInstance[method]();
      } catch (e) {
        // Silent fail for internal methods
      }
    }
  }

  return true;
};

/**
 * Emergency fallback that restarts drawing mode
 */
export const restartDrawingMode = (
  map: L.Map,
  shapeName: string,
  options: any = { snappable: false }
): void => {
  map.pm.disableDraw();
  setTimeout(() => {
    map.pm.enableDraw(shapeName, options);
  }, 50);
};

/**
 * Validates PM drawing state consistency
 */
export const validateDrawingState = (
  drawInstance: PMDrawInstance
): {
  valid: boolean;
  issues: string[];
} => {
  const issues: string[] = [];

  if (!drawInstance._layer) {
    issues.push("Missing drawing layer");
  }

  if (!drawInstance._markers || drawInstance._markers.length === 0) {
    issues.push("No drawing markers found");
  }

  const layer = drawInstance._layer;
  if (layer) {
    const coords = (layer as any).getLatLngs?.();
    const markerCount = drawInstance._markers?.length || 0;

    if (coords && coords.length !== markerCount) {
      issues.push(`Coordinate/marker mismatch: ${coords.length} coords vs ${markerCount} markers`);
    }
  }

  return {
    valid: issues.length === 0,
    issues,
  };
};
